import React, { useRef, useEffect, useState } from "react";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../../NavBars/ActionToolbar";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import ssLogo from "../../../../assets/images/sslogo.png";
import axios from "axios";
import io from "socket.io-client";
import { useDbContext } from "../../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../../app/permissionsSlice";

function RateClassReg({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);

  const rateClassInputRef = useRef(null);
  const touchpercInputRef = useRef(null);
  const saveBtnRef = useRef(null);

  const [saveDisabled, setSaveDisabled] = useState(false);
  const [rateClassData, setRateClassData] = useState({
    rateClass: "",
    touchperc: 0,
  });
  const [rateclassid, setrateclassid] = useState("");
  const [rateClassalldata, setRateClassalldata] = useState([]);

  // Navigation ref handling for ActionToolbar
  const updateNavRefs = (refs) => {
    if (refs.saveButtonRef) {
      saveBtnRef.current = refs.saveButtonRef.current;
    } else if (refs.saveBtnRef) {
      saveBtnRef.current = refs.saveBtnRef.current;
    }
  };

  const fetchrateClass = async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/selectall/${dbCode}`
      );
      const data = response.data || [];
      setRateClassalldata(data);
    } catch (error) {
      console.error("Error fetching rateClass values:", error.message);
    }
  };

  useEffect(() => {
    fetchrateClass();

    // Listen to real-time WebSockets events
    const socket = io(apiBaseUrl);
    socket.on("RateClassUpdated", fetchrateClass);
    socket.on("rateUpdated", fetchrateClass);

    return () => {
      socket.off("RateClassUpdated", fetchrateClass);
      socket.off("rateUpdated", fetchrateClass);
      socket.disconnect();
    };
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    setTimeout(() => {
      rateClassInputRef.current?.focus?.();
    }, 150);
  }, []);

  const handleClear = () => {
    setRateClassData({
      rateClass: "",
      touchperc: 0,
    });
    setrateclassid("");
    fetchrateClass();
    setTimeout(() => {
      rateClassInputRef.current?.focus?.();
    }, 100);
  };

  const handleSelectRecord = (record) => {
    setrateclassid(String(record.id));
    setRateClassData({
      rateClass: record.rateClass || "",
      touchperc: record.touchPercent == null ? 0 : record.touchPercent,
    });
  };

  const handleSubmit = async () => {
    if (!rateClassData.rateClass) {
      alert("Please enter a Rate Class Name");
      return;
    }

    const isDuplicate = rateClassalldata.some(
      (item) =>
        item.rateClass.toLowerCase() === rateClassData.rateClass.toLowerCase()
    );

    if (isDuplicate) {
      alert("Duplicate entry. Rate class already exists.");
      return;
    }

    setSaveDisabled(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/main/insertrateclass/${dbCode}`,
        rateClassData
      );

      if (response.status === 200) {
        alert("Record Saved Successfully!");
        handleClear();
        fetchrateClass();
      } else {
        alert("Error submitting data.");
      }
    } catch (error) {
      console.error("Error saving rate class:", error.message);
      alert("Error submitting data.");
    } finally {
      setSaveDisabled(false);
    }
  };

  const handleEdit = async () => {
    if (!rateclassid) {
      alert("Please select a Rate Class to edit");
      return;
    }
    if (!rateClassData.rateClass) {
      alert("Please enter a valid Rate Class Name");
      return;
    }

    try {
      const response = await axios.post(
        `${apiBaseUrl}/main/updaterateclass/${rateclassid}/${dbCode}`,
        rateClassData
      );
      if (response.status === 200) {
        alert("Entry Updated Successfully!");
        handleClear();
        fetchrateClass();
      } else {
        alert("Error updating data.");
      }
    } catch (error) {
      console.error("Error updating rate class:", error.message);
      alert("Error updating data.");
    }
  };

  const handleDelete = async () => {
    if (!rateclassid) {
      alert("Please select a Rate Class to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${rateClassData.rateClass}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${apiBaseUrl}/main/spdeleterateclass/${rateclassid}/${dbCode}`
      );
      if (response.status === 200) {
        alert("Record Deleted Successfully!");
        handleClear();
        fetchrateClass();
      } else {
        alert("Error deleting data.");
      }
    } catch (error) {
      console.error("Error deleting rate class:", error.message);
      alert("Error deleting data.");
    }
  };

  const handleKeyDown = (event, nextInputRef) => {
    if (event.key === "Enter" || event.key === "Return") {
      event.preventDefault();
      nextInputRef?.current?.focus?.();
    }
  };

  const rateClassComboOptions = rateClassalldata.map((rc) => ({
    label: rc.rateClass || "",
    value: String(rc.id || ""),
    touchPercent: rc.touchPercent,
  }));

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 font-Inter select-none"
      style={{ zIndex: zIndex || 101, fontFamily: "Inter, sans-serif" }}
    >
      <div className="bg-white font-normal w-[580px] max-w-[92vw] h-auto rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <WindowHeader
          title="Rate Class Registration"
          logo={ssLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Toolbar */}
        <div className="w-full flex-shrink-0">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#EAF0FC"
            size="small"
            iconSize={16}
            showSave={true}
            showPrint={false}
            showClear={true}
            showFind={false}
            showEdit={true}
            showDelete={true}
            showExit={true}
            showNavigation={false}
            saveDisabled={!!rateclassid || saveDisabled}
            editDisabled={!rateclassid}
            deleteDisabled={!rateclassid}
            dataFound={!!rateclassid}
            onSave={handleSubmit}
            onClear={handleClear}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onExit={onClose}
            onRefsUpdate={updateNavRefs}
          />
        </div>

        {/* Main Content Body */}
        <div className="w-full flex-1 p-3.5 bg-slate-50 flex gap-4 items-start">
          {/* LEFT COLUMN: Add / Edit Form */}
          <div className="flex-1 bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-2.5">
            <h3 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider m-0">
              ADD / EDIT RATE CLASS
            </h3>

            {/* Rate Class Name */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Rate Class Name
              </label>
              <SherComboBox
                comboRef={rateClassInputRef}
                options={rateClassComboOptions}
                value={rateClassData.rateClass}
                onSelect={(opt) => {
                  const found = rateClassalldata.find((rc) => String(rc.id) === String(opt.value));
                  if (found) {
                    handleSelectRecord(found);
                  } else {
                    setRateClassData((prev) => ({ ...prev, rateClass: opt.label }));
                  }
                }}
                onInputChange={(text) => {
                  setRateClassData((prev) => ({ ...prev, rateClass: text }));
                }}
                onFreeInput={(text) => {
                  setRateClassData((prev) => ({ ...prev, rateClass: text }));
                }}
                placeholder="e.g. 916 GOLD"
                showArrow={true}
                clearable={false}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px" }}
                onKeyDown={(e) => handleKeyDown(e, touchpercInputRef)}
              />
            </div>

            {/* Touch % (Purity) */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                  Touch % (Purity)
                </label>
                <span className="text-[10.5px] font-semibold text-slate-400">
                  0 - 100
                </span>
              </div>
              <div className="relative flex items-center">
                <input
                  type="number"
                  ref={touchpercInputRef}
                  value={rateClassData.touchperc}
                  onChange={(e) =>
                    setRateClassData({ ...rateClassData, touchperc: e.target.value })
                  }
                  placeholder="0"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Return") {
                      e.preventDefault();
                      rateclassid ? handleEdit() : handleSubmit();
                    }
                  }}
                  className="w-full h-8 px-2.5 pr-7 text-xs font-medium border border-slate-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 bg-slate-50/50"
                />
                <span className="absolute right-2.5 text-xs font-semibold text-slate-400 pointer-events-none">
                  %
                </span>
              </div>
              <p className="text-[10.5px] text-slate-400 m-0">
                Enter the purity percentage for this rate class
              </p>
            </div>

            {/* Save / Update Button */}
            <button
              type="button"
              ref={saveBtnRef}
              onClick={rateclassid ? handleEdit : handleSubmit}
              disabled={saveDisabled}
              className="w-full mt-1 py-1.5 px-3 bg-[#1a3abf] hover:bg-[#142e99] active:bg-[#0f2378] text-white font-bold text-xs rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <span>{rateclassid ? "Update Rate Class" : "Save Rate Class"}</span>
            </button>
          </div>

          {/* RIGHT COLUMN: Existing Classes List */}
          <div className="w-[220px] shrink-0 bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-2.5">
            <h3 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider m-0">
              EXISTING CLASSES
            </h3>

            {/* Scrollable Cards List */}
            <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto max-h-[150px] pr-0.5 report-scroll">
              {rateClassalldata.length === 0 ? (
                <div className="py-4 text-center text-xs text-slate-400 italic">
                  No rate classes registered
                </div>
              ) : (
                rateClassalldata.map((item) => {
                  const isSelected = String(item.id) === String(rateclassid);
                  const isGold = item.rateClass?.toLowerCase().includes("gold") || item.rateClass?.toLowerCase().includes("916");
                  const isSilver = item.rateClass?.toLowerCase().includes("silver");
                  const purity = item.touchPercent == null ? 0 : item.touchPercent;

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelectRecord(item)}
                      className={`flex items-center justify-between p-1.5 px-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "bg-blue-50 border-blue-400 shadow-2xs"
                          : "bg-slate-50/70 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isGold
                              ? "bg-amber-400 shadow-2xs shadow-amber-400"
                              : isSilver
                              ? "bg-slate-400 shadow-2xs"
                              : "bg-blue-500"
                          }`}
                        ></span>
                        <span className="font-bold text-xs text-slate-800 tracking-wide">
                          {item.rateClass}
                        </span>
                      </div>

                      <span className="font-semibold text-xs text-slate-500 font-mono">
                        {purity}%
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Count Badge */}
            <div className="pt-1.5 border-t border-slate-100 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>{rateClassalldata.length} classes registered</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RateClassReg;
